import { useEffect, useState } from "react";
import { Calculator, Rocket } from "lucide-react";
import { api } from "../../lib/api";
import { PracticeMonthShare } from "./PracticeMonthShare";

// Hub Progress Recap: quest-map levels cleared + mastered topics per child.
const HUB_TOPIC_NAMES = {
  counting: "Counting", shapes: "Basic Shapes", fractions: "Fractions",
  multiplication: "Multiplication", prealgebra: "Pre-Algebra", geometry: "Geometry",
};

const HubRecap = ({ child }) => {
  const [progress, setProgress] = useState(null);
  useEffect(() => {
    api.get(`/math/${child.child_id}/hub`)
      .then(({ data }) => setProgress(data.progress || {})).catch(() => {});
  }, [child.child_id]);
  if (!progress) return null;
  const cleared = Object.entries(progress)
    .filter(([id, p]) => id.includes("-n") && p.stars > 0).length;
  const mastered = Object.entries(progress)
    .filter(([id, p]) => id.includes("-t") && p.stars >= 3)
    .map(([id]) => HUB_TOPIC_NAMES[id.split("-t")[1]] || id.split("-t")[1]);
  if (!cleared && !mastered.length) return null;
  return (
    <p className="flex items-center gap-1.5 flex-wrap text-xs font-bold text-muted-foreground"
      data-testid={`hub-recap-${child.child_id}`}>
      <Rocket className="h-3.5 w-3.5 text-cyan-600 shrink-0" aria-hidden="true" />
      Practice Hub: {cleared} quest level{cleared === 1 ? "" : "s"} cleared
      {mastered.length > 0 && <> · Mastered: {mastered.join(", ")}</>}
    </p>
  );
};

// Parent-facing math progress: per-concept difficulty levels, answer effort,
// a 14-day activity strip and earned math badges — one block per child.
// Renders nothing when the family isn't math-entitled or no child has
// practiced yet, so free dashboards stay uncluttered.

const LevelDots = ({ level }) => (
  <span className="inline-flex gap-1" aria-label={`Level ${level} of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <span key={n} className={`h-2.5 w-2.5 rounded-full ${
        n <= level ? "bg-violet-500" : "bg-muted border border-border"}`} />
    ))}
  </span>
);

const ActivityStrip = ({ activity }) => {
  const max = Math.max(1, ...activity.map((d) => d.count));
  return (
    <div className="flex items-end gap-1 h-10" data-testid="math-activity-strip"
      aria-label="Practice answers per day, last 14 days">
      {activity.map((d) => (
        <div key={d.day} title={`${d.day}: ${d.count}`}
          className={`flex-1 rounded-sm ${d.count ? "bg-emerald-400" : "bg-muted"}`}
          style={{ height: `${d.count ? Math.max(18, (d.count / max) * 100) : 8}%` }} />
      ))}
    </div>
  );
};

const ChildBlock = ({ child, summary }) => (
  <div className="rounded-xl border border-border p-4 space-y-3"
    data-testid={`math-progress-child-${child.child_id}`}>
    <div className="flex items-center gap-2 flex-wrap">
      <p className="text-sm font-bold">{child.display_name}</p>
      <span className="text-xs text-muted-foreground">
        {summary.sessions} session{summary.sessions === 1 ? "" : "s"} ·{" "}
        {summary.answers.correct} solved
      </span>
    </div>
    {summary.concepts.length > 0 && (
      <div className="space-y-1.5">
        {summary.concepts.map((c) => (
          <div key={c.concept} className="flex items-center justify-between gap-2 text-xs"
            data-testid={`math-progress-concept-${child.child_id}`}>
            <span className="font-bold truncate">{c.concept}</span>
            <LevelDots level={c.level} />
          </div>
        ))}
      </div>
    )}
    <ActivityStrip activity={summary.activity} />
    <HubRecap child={child} />
    <div className="flex flex-wrap gap-1.5">
      <PracticeMonthShare child={child} surface="math" />
      <PracticeMonthShare child={child} surface="speech" />
      <PracticeMonthShare child={child} surface="story" />
    </div>
    {summary.badges.length > 0 && (
      <div className="flex flex-wrap gap-1.5" data-testid={`math-progress-badges-${child.child_id}`}>
        {summary.badges.map((b) => (
          <span key={b.key} title={b.note}
            className="rounded-full bg-amber-50 border border-amber-200 text-[11px] font-bold px-2 py-0.5">
            {b.icon} {b.name}
          </span>
        ))}
      </div>
    )}
  </div>
);

export const MathProgress = () => {
  const [kids, setKids] = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await api.get("/math/access");
        if (!data.math_entitled || !data.children?.length) { if (live) setKids([]); return; }
        const rows = await Promise.all(data.children.map(async (c) => {
          try {
            const { data: s } = await api.get(`/math/${c.child_id}/progress-summary`);
            return { child: c, summary: s };
          } catch { return null; }
        }));
        if (live) {
          setKids(rows.filter((r) => r && (r.summary.sessions > 0
            || r.summary.concepts.length > 0 || r.summary.badges.length > 0)));
        }
      } catch { if (live) setKids([]); }
    })();
    return () => { live = false; };
  }, []);

  if (!kids?.length) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4"
      data-testid="math-progress-panel">
      <h3 className="text-sm font-bold font-display flex items-center gap-2">
        <Calculator className="h-4 w-4 text-secondary" aria-hidden="true" /> Math progress
      </h3>
      <div className="grid sm:grid-cols-2 gap-4">
        {kids.map(({ child, summary }) => (
          <ChildBlock key={child.child_id} child={child} summary={summary} />
        ))}
      </div>
    </div>
  );
};
